import {
  Grid,
  TextField,
  Button,
  Snackbar,
  Alert,
  CircularProgress,
  Box,
  Typography,
  Divider,
  IconButton,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import EmailIcon from "@mui/icons-material/Email";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { register } from "../../../Redux/Auth/Action";
import { useEffect, useState } from "react";
import axios from "axios";
import { useTheme } from "../../../contexts/ThemeContext";

export default function RegisterUserForm({ onSwitchToLogin }) {
  const baseUrl = process.env.REACT_APP_API_BASE_URL;
  const { isDarkMode } = useTheme();

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { auth } = useSelector((store) => store);

  // ── Verification channel toggle: "whatsapp" | "email" ────────
  const [verifyChannel, setVerifyChannel] = useState("whatsapp");

  // ── WhatsApp OTP state ─────────────────────────────────────
  const [mobile, setMobile] = useState("");
  const [mobileVerified, setMobileVerified] = useState(false);
  const [whatsappOtpSent, setWhatsappOtpSent] = useState(false);
  const [whatsappOtpLoading, setWhatsappOtpLoading] = useState(false);
  const [verifyingWhatsappOtp, setVerifyingWhatsappOtp] = useState(false);
  const [whatsappOtpInputs, setWhatsappOtpInputs] = useState(["", "", "", "", "", ""]);

  // ── Email OTP state ──────────────────────────────────────────
  const [email, setEmail] = useState("");
  const [emailVerified, setEmailVerified] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [otpInputs, setOtpInputs] = useState(["", "", "", "", ""]);

  // ── Post-verification "other contact" field ──────────────────
  // After WhatsApp verify → user enters email
  // After Email verify    → user enters mobile
  const [otherEmail, setOtherEmail] = useState("");
  const [otherMobile, setOtherMobile] = useState("");

  // ── Registration form state ───────────────────────────────────
  const [formData, setFormData] = useState({ firstName: "", lastName: "", password: "" });
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");
  const [registerLoading, setRegisterLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // ── Snackbar ──────────────────────────────────────────────────
  const [snackBarMessage, setSnackBarMessage] = useState("");
  const [snackBarOpen, setSnackBarOpen] = useState(false);
  const [snackBarSeverity, setSnackBarSeverity] = useState("success");

  // Derived
  const isVerified = emailVerified || mobileVerified;

  // ── Helpers ───────────────────────────────────────────────────
  const handleSnackbar = (msg, severity = "success") => {
    setSnackBarMessage(msg);
    setSnackBarSeverity(severity);
    setSnackBarOpen(true);
  };
  const handleCloseSnackbar = () => setSnackBarOpen(false);

  const textFieldStyles = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: isDarkMode ? "rgba(255, 255, 255, 0.05)" : "#ffffff",
      "& fieldset": {
        borderColor: isDarkMode ? "rgba(255, 255, 255, 0.23)" : "rgba(0, 0, 0, 0.23)",
      },
      "&:hover fieldset": {
        borderColor: isDarkMode ? "rgba(255, 255, 255, 0.4)" : "rgba(0, 0, 0, 0.4)",
      },
      "&.Mui-focused fieldset": { borderColor: "#9155FD" },
    },
    "& .MuiInputLabel-root": {
      color: isDarkMode ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.6)",
    },
    "& .MuiInputBase-input": {
      color: isDarkMode ? "white !important" : "black",
      "&::placeholder": {
        color: isDarkMode ? "rgba(255, 255, 255, 0.5)" : "rgba(0, 0, 0, 0.4)",
        opacity: 1,
      },
    },
    "& .MuiFormHelperText-root": {
      color: isDarkMode ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.6)",
    },
  };

  const btnStyle = {
    padding: ".8rem 0",
    background: "linear-gradient(45deg, #9155FD 30%, #A67BFF 90%)",
    boxShadow: "0 3px 5px 2px rgba(145, 85, 253, .3)",
    "&:hover": { background: "linear-gradient(45deg, #7E3FF2 30%, #9155FD 90%)" },
  };

  const whatsappBtnStyle = {
    padding: ".8rem 0",
    background: "linear-gradient(45deg, #128C7E 30%, #25D366 90%)",
    boxShadow: "0 3px 5px 2px rgba(37, 211, 102, .25)",
    "&:hover": { background: "linear-gradient(45deg, #075E54 30%, #128C7E 90%)" },
  };

  // ── Channel toggle ────────────────────────────────────────────
  const handleChannelChange = (_, newChannel) => {
    if (!newChannel || isVerified) return;
    setVerifyChannel(newChannel);
    if (newChannel === "email") {
      setWhatsappOtpSent(false);
      setWhatsappOtpInputs(["", "", "", "", "", ""]);
    }
    if (newChannel === "whatsapp") {
      setOtpSent(false);
      setOtpInputs(["", "", "", "", ""]);
    }
  };

  // ── WhatsApp OTP handlers ─────────────────────────────────────
  const handleSendWhatsappOtp = async () => {
    if (!/^[6-9]\d{9}$/.test(mobile)) {
      return handleSnackbar("Please enter a valid 10-digit Indian mobile number.", "error");
    }
    try {
      setWhatsappOtpLoading(true);
      const res = await axios.post(`${baseUrl}/auth/send-whatsapp-otp`, { mobile });
      handleSnackbar(res.data.message || "OTP sent on WhatsApp!", "success");
      setWhatsappOtpSent(true);
    } catch (err) {
      handleSnackbar(err.response?.data?.message || "Failed to send WhatsApp OTP. Please try again.", "error");
    } finally {
      setWhatsappOtpLoading(false);
    }
  };

  const handleVerifyWhatsappOtp = async () => {
    const enteredOtp = whatsappOtpInputs.join("");
    if (enteredOtp.length !== 6) {
      return handleSnackbar("Please enter the complete 6-digit OTP.", "error");
    }
    try {
      setVerifyingWhatsappOtp(true);
      const res = await axios.post(`${baseUrl}/auth/verify-whatsapp-otp`, { mobile, otp: enteredOtp });
      if (res.data.success === true) {
        setMobileVerified(true);
        handleSnackbar("Mobile verified successfully! 🎉", "success");
      } else {
        handleSnackbar(res.data.message || "OTP verification failed.", "error");
      }
    } catch (err) {
      handleSnackbar(err.response?.data?.message || "OTP verification failed.", "error");
    } finally {
      setVerifyingWhatsappOtp(false);
    }
  };

  const handleWhatsappOtpChange = (index, value) => {
    if (/^\d?$/.test(value)) {
      const newOtp = [...whatsappOtpInputs];
      newOtp[index] = value;
      setWhatsappOtpInputs(newOtp);
      if (value && index < 5) {
        const nextInput = document.getElementById(`wotp-${index + 1}`);
        if (nextInput) nextInput.focus();
      }
    }
  };

  const handleWhatsappOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !whatsappOtpInputs[index] && index > 0) {
      const prevInput = document.getElementById(`wotp-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  // ── Email OTP handlers ────────────────────────────────────────
  const handleSendOtp = async () => {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) || email.startsWith("a@")) {
      return handleSnackbar("Please enter a valid and acceptable email.", "error");
    }
    try {
      setOtpLoading(true);
      const res = await axios.post(`${baseUrl}/auth/send-otp`, { email });
      handleSnackbar(res.data.message || "OTP sent successfully!", "success");
      setOtpSent(true);
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.response?.data?.message || "Failed to send OTP.";
      if (errorMsg.toLowerCase().includes("user already exists")) {
        handleSnackbar("User already registered. Please login.", "error");
      } else {
        handleSnackbar(errorMsg, "error");
      }
    } finally {
      setOtpLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    const enteredOtp = otpInputs.join("");
    if (enteredOtp.length !== 5) {
      return handleSnackbar("Please enter a valid 5-digit OTP.", "error");
    }
    try {
      setVerifyingOtp(true);
      const res = await axios.post(`${baseUrl}/auth/verify-otp`, { email, otp: enteredOtp });
      if (res.data.success === true) {
        setEmailVerified(true);
        handleSnackbar("Email verified successfully!", "success");
      } else {
        handleSnackbar(res.data.message || "OTP verification failed.", "error");
      }
    } catch (err) {
      handleSnackbar(err.response?.data?.message || "OTP verification failed.", "error");
    } finally {
      setVerifyingOtp(false);
    }
  };

  const handleOtpChange = (index, value) => {
    if (/^\d?$/.test(value)) {
      const newOtp = [...otpInputs];
      newOtp[index] = value;
      setOtpInputs(newOtp);
      if (value && index < 4) {
        const nextInput = document.getElementById(`otp-${index + 1}`);
        if (nextInput) nextInput.focus();
      }
    }
  };

  // ── Form submit ───────────────────────────────────────────────
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z\d]).{8,}$/;

    if (!passwordRegex.test(formData.password)) {
      setPasswordError(
        "Password must be at least 8 characters, include uppercase, lowercase, number & special character."
      );
      return;
    } else {
      setPasswordError("");
    }

    if (formData.password !== confirmPassword) {
      setConfirmPasswordError("Passwords do not match.");
      return;
    } else {
      setConfirmPasswordError("");
    }

    // Determine final email and mobile to send
    // mobileVerified → primary is mobile, email comes from otherEmail field
    // emailVerified  → primary is email, mobile comes from otherMobile field
    const finalEmail = mobileVerified ? otherEmail : email;
    const finalMobile = emailVerified ? otherMobile : mobile;

    try {
      setRegisterLoading(true);
      await dispatch(
        register({
          ...formData,
          email: finalEmail,
          emailVerified,
          mobileVerified,
          mobile: mobileVerified ? mobile : (otherMobile || undefined),
        })
      );
      setTimeout(() => {
        if (auth.error && !localStorage.getItem("jwt")) {
          handleSnackbar(auth.error, "error");
        }
      }, 100);
    } finally {
      setRegisterLoading(false);
    }
  };

  useEffect(() => {
    if (auth.user && !auth.isLoading) {
      handleSnackbar("Register Success", "success");
    }
  }, [auth.user]);

  // ── Render ────────────────────────────────────────────────────
  return (
    <Box sx={{ p: { xs: 0, md: 1 } }}>
      <Typography
        variant="h5"
        sx={{ mb: 2, fontWeight: 600, color: isDarkMode ? "white" : "black", textAlign: "center" }}
      >
        Create Account
      </Typography>
      <Typography
        variant="body2"
        sx={{
          mb: 3,
          color: isDarkMode ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.6)",
          textAlign: "center",
        }}
      >
        Join us and start shopping today
      </Typography>

      <form onSubmit={handleFormSubmit}>
        <Grid container spacing={2}>

          {/* ─── Verification channel toggle (hidden after verification) ─── */}
          {!isVerified && (
            <Grid item xs={12}>
              <Typography
                variant="body2"
                sx={{
                  mb: 1,
                  color: isDarkMode ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.55)",
                  textAlign: "center",
                  fontSize: "0.82rem",
                }}
              >
                Choose how to verify your identity
              </Typography>
              <ToggleButtonGroup
                value={verifyChannel}
                exclusive
                onChange={handleChannelChange}
                fullWidth
                size="small"
                sx={{
                  "& .MuiToggleButton-root": {
                    borderColor: isDarkMode ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.2)",
                    color: isDarkMode ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.6)",
                    textTransform: "none",
                    fontWeight: 500,
                    gap: 0.8,
                    "&.Mui-selected": {
                      backgroundColor: verifyChannel === "whatsapp"
                        ? "rgba(37,211,102,0.15)"
                        : "rgba(145,85,253,0.15)",
                      color: verifyChannel === "whatsapp" ? "#25D366" : "#9155FD",
                      borderColor: verifyChannel === "whatsapp" ? "#25D366" : "#9155FD",
                      "&:hover": {
                        backgroundColor: verifyChannel === "whatsapp"
                          ? "rgba(37,211,102,0.22)"
                          : "rgba(145,85,253,0.22)",
                      },
                    },
                  },
                }}
              >
                <ToggleButton value="whatsapp">
                  <WhatsAppIcon fontSize="small" />
                  WhatsApp OTP
                </ToggleButton>
                <ToggleButton value="email">
                  <EmailIcon fontSize="small" />
                  Email OTP
                </ToggleButton>
              </ToggleButtonGroup>
            </Grid>
          )}

          {/* ─── WHATSAPP OTP FLOW ─────────────────────────────────── */}
          {verifyChannel === "whatsapp" && !isVerified && (
            <>
              {/* Mobile input */}
              <Grid item xs={12}>
                <TextField
                  label="WhatsApp Mobile Number"
                  value={mobile}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, "").slice(0, 10);
                    setMobile(val);
                  }}
                  disabled={whatsappOtpSent}
                  required
                  fullWidth
                  placeholder="10-digit mobile (e.g. 9876543210)"
                  inputProps={{ maxLength: 10, inputMode: "numeric" }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Typography sx={{ color: isDarkMode ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.5)", fontSize: "0.9rem" }}>
                          +91
                        </Typography>
                      </InputAdornment>
                    ),
                  }}
                  sx={textFieldStyles}
                />
              </Grid>

              {/* Send OTP button */}
              {!whatsappOtpSent && (
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={handleSendWhatsappOtp}
                    disabled={whatsappOtpLoading}
                    sx={whatsappBtnStyle}
                  >
                    {whatsappOtpLoading ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <WhatsAppIcon fontSize="small" />
                        SEND OTP ON WHATSAPP
                      </Box>
                    )}
                  </Button>
                </Grid>
              )}

              {/* OTP boxes (6-digit) */}
              {whatsappOtpSent && (
                <>
                  <Grid item xs={12}>
                    <Typography
                      variant="body2"
                      sx={{
                        textAlign: "center",
                        color: isDarkMode ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.6)",
                        mb: 1,
                      }}
                    >
                      Enter the 6-digit OTP sent to{" "}
                      <strong style={{ color: "#25D366" }}>+91 {mobile}</strong> on WhatsApp
                    </Typography>
                    <Box display="flex" justifyContent="center" gap={0.8}>
                      {whatsappOtpInputs.map((digit, i) => (
                        <TextField
                          key={i}
                          id={`wotp-${i}`}
                          value={digit}
                          onChange={(e) => handleWhatsappOtpChange(i, e.target.value)}
                          onKeyDown={(e) => handleWhatsappOtpKeyDown(i, e)}
                          inputProps={{ maxLength: 1, style: { textAlign: "center" } }}
                          sx={{ width: "45px", ...textFieldStyles }}
                        />
                      ))}
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={handleVerifyWhatsappOtp}
                      disabled={verifyingWhatsappOtp}
                      sx={whatsappBtnStyle}
                    >
                      {verifyingWhatsappOtp ? <CircularProgress size={24} color="inherit" /> : "Verify OTP"}
                    </Button>
                  </Grid>
                  {/* Resend link */}
                  <Grid item xs={12}>
                    <Box textAlign="center">
                      <Button
                        size="small"
                        variant="text"
                        onClick={() => {
                          setWhatsappOtpSent(false);
                          setWhatsappOtpInputs(["", "", "", "", "", ""]);
                        }}
                        sx={{
                          textTransform: "none",
                          color: isDarkMode ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.45)",
                          fontSize: "0.78rem",
                        }}
                      >
                        Didn't receive? Change number or resend
                      </Button>
                    </Box>
                  </Grid>
                </>
              )}
            </>
          )}

          {/* ─── EMAIL OTP FLOW ───────────────────────────────────────── */}
          {verifyChannel === "email" && !isVerified && (
            <>
              {/* Email input – shown inline under Email OTP tab */}
              <Grid item xs={12}>
                <TextField
                  label="Email"
                  type="email"
                  value={email}
                  onChange={(e) => !otpSent && setEmail(e.target.value)}
                  disabled={otpSent}
                  required
                  fullWidth
                  placeholder="you@example.com"
                  sx={textFieldStyles}
                />
              </Grid>

              {/* Send OTP button */}
              {!otpSent && (
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={handleSendOtp}
                    disabled={otpLoading}
                    sx={btnStyle}
                  >
                    {otpLoading ? <CircularProgress size={24} color="inherit" /> : "Send Verification Code"}
                  </Button>
                </Grid>
              )}

              {/* OTP boxes (5-digit) */}
              {otpSent && (
                <>
                  <Grid item xs={12}>
                    <Typography
                      variant="body2"
                      sx={{
                        textAlign: "center",
                        color: isDarkMode ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.6)",
                        mb: 1,
                      }}
                    >
                      Enter the 5-digit code sent to{" "}
                      <strong style={{ color: "#9155FD" }}>{email}</strong>
                    </Typography>
                    <Box display="flex" justifyContent="center" gap={1}>
                      {otpInputs.map((digit, i) => (
                        <TextField
                          key={i}
                          id={`otp-${i}`}
                          value={digit}
                          onChange={(e) => handleOtpChange(i, e.target.value)}
                          inputProps={{ maxLength: 1, style: { textAlign: "center" } }}
                          sx={{ width: "50px", ...textFieldStyles }}
                        />
                      ))}
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={handleVerifyOtp}
                      disabled={verifyingOtp}
                      sx={btnStyle}
                    >
                      {verifyingOtp ? <CircularProgress size={24} color="inherit" /> : "Verify Code"}
                    </Button>
                  </Grid>
                </>
              )}
            </>
          )}

          {/* ─── Verified banner ──────────────────────────────────────── */}
          {isVerified && (
            <Grid item xs={12}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  px: 2,
                  py: 1,
                  borderRadius: 2,
                  backgroundColor: mobileVerified
                    ? "rgba(37,211,102,0.12)"
                    : "rgba(145,85,253,0.12)",
                  border: mobileVerified ? "1px solid #25D366" : "1px solid #9155FD",
                }}
              >
                <CheckCircleIcon
                  fontSize="small"
                  sx={{ color: mobileVerified ? "#25D366" : "#9155FD" }}
                />
                <Typography
                  variant="body2"
                  sx={{ color: mobileVerified ? "#25D366" : "#9155FD", fontWeight: 500 }}
                >
                  {mobileVerified
                    ? `Mobile +91 ${mobile} verified ✓`
                    : `Email ${email} verified ✓`}
                </Typography>
              </Box>
            </Grid>
          )}

          {/* ─── Post-verification: collect the OTHER contact detail ─── */}
          {isVerified && mobileVerified && (
            // Verified via WhatsApp → now ask for email
            <Grid item xs={12}>
              <TextField
                label="Email Address"
                type="email"
                value={otherEmail}
                onChange={(e) => setOtherEmail(e.target.value)}
                required
                fullWidth
                placeholder="you@example.com"
                helperText="Required to complete your account"
                sx={textFieldStyles}
              />
            </Grid>
          )}

          {isVerified && emailVerified && (
            // Verified via Email → now ask for mobile number
            <Grid item xs={12}>
              <TextField
                label="Mobile Number (optional)"
                value={otherMobile}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, "").slice(0, 10);
                  setOtherMobile(val);
                }}
                fullWidth
                placeholder="10-digit mobile (e.g. 9876543210)"
                inputProps={{ maxLength: 10, inputMode: "numeric" }}
                helperText="Optional — used for order updates"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Typography sx={{ color: isDarkMode ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.5)", fontSize: "0.9rem" }}>
                        +91
                      </Typography>
                    </InputAdornment>
                  ),
                }}
                sx={textFieldStyles}
              />
            </Grid>
          )}

          {/* ─── Name + Password section (shown after verification) ────── */}
          {isVerified && (
            <>
              <Grid item xs={6}>
                <TextField
                  label="First Name"
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  fullWidth
                  required
                  sx={textFieldStyles}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Last Name"
                  value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                  fullWidth
                  required
                  sx={textFieldStyles}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={(e) => {
                    setFormData({ ...formData, password: e.target.value });
                    if (passwordError) setPasswordError("");
                  }}
                  error={!!passwordError}
                  helperText={passwordError}
                  fullWidth
                  required
                  sx={textFieldStyles}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          sx={{ color: isDarkMode ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.6)" }}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Confirm Password"
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    if (confirmPasswordError) setConfirmPasswordError("");
                  }}
                  error={!!confirmPasswordError}
                  helperText={confirmPasswordError}
                  fullWidth
                  required
                  sx={textFieldStyles}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          edge="end"
                          sx={{ color: isDarkMode ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.6)" }}
                        >
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  color="primary"
                  disabled={!isVerified || registerLoading}
                  sx={btnStyle}
                >
                  {registerLoading ? <CircularProgress size={24} color="inherit" /> : "Create Account"}
                </Button>
              </Grid>
            </>
          )}
        </Grid>
      </form>

      <Divider
        sx={{ my: 3, borderColor: isDarkMode ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.12)" }}
      />

      <Box display="flex" justifyContent="center" alignItems="center">
        <span className={isDarkMode ? "text-gray-300" : "text-gray-700"}>
          Already have an account?
        </span>
        <Button
          onClick={onSwitchToLogin}
          size="small"
          sx={{
            color: isDarkMode ? "#A67BFF" : "#9155FD",
            fontWeight: 600,
            textTransform: "none",
            "&:hover": {
              backgroundColor: isDarkMode
                ? "rgba(166, 123, 255, 0.1)"
                : "rgba(145, 85, 253, 0.1)",
            },
          }}
        >
          Sign In
        </Button>
      </Box>

      <Snackbar open={snackBarOpen} autoHideDuration={4000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity={snackBarSeverity} sx={{ width: "100%" }}>
          {snackBarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}
